import React, { useState } from 'react';
import { X, Plus, AlertCircle } from 'lucide-react';
import { createOrganizationWithCheck } from '../../../lib/supabase/contacts';
import { useAuthStore } from '../../../store/authStore';
import { useEntity } from '../../../hooks/useEntity'; 
import type { Organization } from '../../../types/contacts';
import { normalizeWebsiteUrl } from '../../../lib/validation';

interface OrganizationFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function OrganizationForm({ onClose, onSuccess }: OrganizationFormProps) {
  const { user } = useAuthStore();
  const { entity } = useEntity();
  const [formData, setFormData] = useState({
    name: '',
    type: 'client',
    domain: '',
    address: '',
    notes: ''
  });

  const [duplicates, setDuplicates] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateForm = () => {
    setValidationError(null);

    if (!formData.name.trim()) {
      setValidationError('Organization name is required');
      return false;
    }

    if (!user?.id) {
      setValidationError('You must be logged in to create an organization');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!entity?.id) {
      setError('No entity found. Please set up your company profile first.');
      return;
    }

    if (!user?.id) {
      setError('You must be logged in to create an organization.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const domain = formData.domain ? normalizeWebsiteUrl(formData.domain) : '';
      
      console.log('Creating organization with data:', {
        name: formData.name,
        type: formData.type,
        domain,
        entity_id: entity.id
      });

      const result = await createOrganizationWithCheck({
        name: formData.name.trim(),
        type: formData.type as 'client' | 'supplier' | 'partner',
        domain: domain,
        address: formData.address.trim() || null,
        entity_id: entity.id
      });

      if (result.duplicates.length > 0) {
        console.log('Found duplicate organizations:', result.duplicates);
        setDuplicates(result.duplicates);
        return;
      }

      if (result.data) {
        console.log('Organization created successfully:', result.data);
        onSuccess();
      } else {
        throw new Error('Failed to create organization');
      }
    } catch (error) {
      console.error('Failed to create organization:', error);
      setError('Failed to create organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderDuplicateWarning = () => {
    if (duplicates.length === 0) return null;

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800">Potential Duplicates Found</h4>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Similar organizations already exist:</p>
              <ul className="mt-1 list-disc list-inside">
                {duplicates.map(org => (
                  <li key={org.id}>{org.name}</li>
                ))}
              </ul>
            </div>
            <div className="mt-3 flex space-x-3">
              <button
                type="button"
                onClick={() => setDuplicates([])}
                className="text-sm text-yellow-800 hover:text-yellow-900"
              >
                Create Anyway
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-yellow-800 hover:text-yellow-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Add New Organization</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {validationError && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {validationError}
          </div>
        )}

        {renderDuplicateWarning()}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Organization Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="client">Client</option>
              <option value="internal">Your Company (Internal)</option>
              <option value="supplier">Supplier</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Enter organization address"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Organization</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}